import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { LuCopy, LuCheck } from "react-icons/lu";
import WpDynamicTable from "../../Components/WhatsappTable/WpDynamicTable";
import { fetchAllClients } from "../../Redux/WhatsappSlice/Client";
import Loader from "../../Components/Loader/Loader";

const ReadMoreCell = ({ text, limit = 30, isAllExpanded = false }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!text || text === "-") return <span>-</span>;

  const handleCopy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  const isTruncated = text.length > limit;
  const currentlyExpanded = isAllExpanded || isExpanded;
  const displayText = isTruncated
    ? currentlyExpanded
      ? text
      : text.slice(0, limit) + "..."
    : text;

  return (
    <div className="relative group flex items-start justify-between w-full min-h-[24px] py-1 pr-6">
      <div className="text-left whitespace-normal break-all w-full">
        <span className="text-gray-800 font-medium text-sm">{displayText}</span>
        {isTruncated && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
            className="text-xs text-blue-600 hover:text-blue-800 font-bold ml-1.5 focus:outline-none cursor-pointer border-none bg-transparent inline-flex items-center"
          >
            {currentlyExpanded ? "Read Less" : "Read More"}
          </button>
        )}
      </div>

      <div className="absolute right-0 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
        <button
          onClick={handleCopy}
          className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-700 focus:outline-none cursor-pointer border border-gray-200 bg-white flex items-center justify-center shadow-sm transition-all"
          title="Copy to clipboard"
        >
          {copied ? (
            <LuCheck className="text-green-600" size={14} />
          ) : (
            <LuCopy size={14} />
          )}
        </button>
      </div>
    </div>
  );
};

const ViewClient = () => {
  const dispatch = useDispatch();
  const { AllClients } = useSelector((state) => state.Client);
  const [expandAll, setExpandAll] = useState(false);

  useEffect(() => {
    dispatch(fetchAllClients());
  }, [dispatch]);

  const allRows =
    AllClients?.data?.map((el, index) => ({
      id: index + 1,
      tokan: el?.CallNo,
      MainId: el._id,
      WhaClientName: el?.WhaClientName,
      Project: el?.Project?.map((p) => p.project_name).join(", ") || "-",
      AdminID: el?.AdminID?.Name || "-",
      CheckConnection: el.CheckConnection ? "True" : "False",
      CheckVersion: el.CheckVersion ? "True" : "False",
      PhysicalAddress: el?.PhysicalAddress || "-",
      StaticIP: el?.StaticIP || "-",
    })) || [];

  const columns = [
    { field: "id", headerName: "No", flex: 0.3, minWidth: 50 },
    { field: "WhaClientName", headerName: "Client Name", flex: 1, minWidth: 150 },
    { field: "AdminID", headerName: "Admin Name", flex: 1, minWidth: 150 },
    {
      field: "Project",
      headerName: "Project Name",
      flex: 2,
      minWidth: 250,
      renderCell: (params) => (
        <ReadMoreCell text={params.row.Project} limit={30} isAllExpanded={expandAll} />
      ),
    },
    { field: "CheckConnection", headerName: "Check Connection", flex: 0.8, minWidth: 130 },
    { field: "CheckVersion", headerName: "Check Version", flex: 0.8, minWidth: 110 },
    {
      field: "PhysicalAddress",
      headerName: "Physical Address",
      flex: 3,
      minWidth: 350,
      renderCell: (params) => (
        <ReadMoreCell text={params.row.PhysicalAddress} limit={30} isAllExpanded={expandAll} />
      ),
    },
    { field: "StaticIP", headerName: "Static IP", flex: 1, minWidth: 130 },
  ];

  if (!AllClients?.data || !AllClients) {
    return <Loader />;
  }

  return (
    <>
      <div className="">
        <div className="title flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
          <h3 className="text-xl font-bold main_title_page text-white px-4 py-2 inline-block rounded">
            View Client
          </h3>
          <div className="flex items-center space-x-2 bg-[#122A47] text-white px-4 py-2 rounded-lg shadow-md self-end sm:self-auto">
            <span className="text-sm font-semibold">Expand All</span>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={expandAll}
                onChange={(e) => setExpandAll(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
            </label>
          </div>
        </div>

        {/* Table */}
        <div className="rounded-lg overflow-x-auto mt-2">
          <WpDynamicTable columns={columns} rows={allRows} height="550px" />
        </div>
      </div>
    </>
  );
};

export default ViewClient;
